import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Typography, Space, Spin, Alert, Button } from 'antd';
import { 
  UserOutlined, 
  ExperimentOutlined, 
  FileTextOutlined, 
  EnvironmentOutlined,
  RobotOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';

const { Title } = Typography;

interface DashboardStats {
  patients: number;
  specimens: number;
  orders: number;
  locations: number;
  automation: number;
  users: number;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    patients: 0,
    specimens: 0,
    orders: 0,
    locations: 0,
    automation: 0,
    users: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all stats in parallel
      const [
        patientsData,
        specimensData,
        pendingOrdersData,
        locationsData,
        usersData
      ] = await Promise.all([
        apiService.getPatients(1, 1), // Get just 1 record to get total count
        apiService.getSpecimens(1, 1), // Get just 1 record to get total count
        apiService.getUniqueAccessions(1, 1, '', 'In Progress'), // Get pending orders count
        apiService.getAllLocations(), // Get flat list of all active locations
        apiService.getUsers(1, 1) // Get just 1 record to get total count
      ]);

      // Extract counts from responses
      const patientsCount = patientsData.totalCount || patientsData.total || 0;
      const specimensCount = specimensData.total || 0;
      const pendingOrdersCount = pendingOrdersData.total || 0; // This is now filtered to pending orders
      const locationsCount = Array.isArray(locationsData) ? locationsData.length : 0; // All active locations
      const usersCount = usersData.total || 0;

      // For automation, we'll use a placeholder for now since there's no automation endpoint
      const automationCount = 0; // TODO: Add automation endpoint when available

      setStats({
        patients: patientsCount,
        specimens: specimensCount,
        orders: pendingOrdersCount, // Now shows only pending orders
        locations: locationsCount,
        automation: automationCount,
        users: usersCount
      });

    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      setError('Failed to load dashboard statistics. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const statCards = [
    {
      title: 'Total Patients',
      value: stats.patients,
      icon: <UserOutlined />,
      color: '#1890ff',
      path: '/patients'
    },
    {
      title: 'Active Specimens',
      value: stats.specimens,
      icon: <ExperimentOutlined />,
      color: '#52c41a',
      path: '/specimens'
    },
    {
      title: 'Pending Orders',
      value: stats.orders,
      icon: <FileTextOutlined />,
      color: '#faad14',
      path: '/orders'
    },
    {
      title: 'Active Locations',
      value: stats.locations,
      icon: <EnvironmentOutlined />,
      color: '#722ed1',
      path: '/locations'
    },
    {
      title: 'Automation Tasks',
      value: stats.automation,
      icon: <RobotOutlined />,
      color: '#eb2f96',
      path: '/automation'
    },
    {
      title: 'Total Users',
      value: stats.users,
      icon: <TeamOutlined />,
      color: '#13c2c2',
      path: '/users'
    }
  ];

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Dashboard</Title>
      
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: '16px' }}
          action={
            <Button 
              type="link" 
              onClick={fetchDashboardStats} 
              style={{ color: '#1890ff', padding: 0, height: 'auto' }}
            >
              Retry
            </Button>
          }
        />
      )}

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Row gutter={[16, 16]}>
          {statCards.map((card, index) => (
            <Col xs={24} sm={12} md={8} lg={8} xl={8} key={index}>
              <Card
                hoverable
                onClick={() => navigate(card.path)}
                style={{ cursor: 'pointer' }}
              >
                <Statistic
                  title={card.title}
                  value={card.value}
                  prefix={React.cloneElement(card.icon, { style: { color: card.color } })}
                />
              </Card>
            </Col>
          ))}
        </Row>

        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card title="Quick Actions">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Card
                    hoverable
                    onClick={() => navigate('/my-tasks')}
                    style={{ cursor: 'pointer' }}
                  >
                    <Space>
                      <FileTextOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                      <span>My Tasks</span>
                    </Space>
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Card
                    hoverable
                    onClick={() => navigate('/admin/specimen-classes')}
                    style={{ cursor: 'pointer' }}
                  >
                    <Space>
                      <ExperimentOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
                      <span>Manage Specimen Classes</span>
                    </Space>
                  </Card>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Space>
    </div>
  );
};

export default Dashboard; 